import ArrowLeft from "@/src/sb/components/icon/ArrowLeft"
import {
  FloatingPortal,
  flip,
  offset,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useInteractions
} from "@floating-ui/react"
import classNames from "classnames"
import { useEffect, useMemo, useState } from "react"
import Skeleton from "react-loading-skeleton"
import "react-loading-skeleton/dist/skeleton.css"
import tinycolor from "tinycolor2"

export type CursorPaginationProps = {
  type?: "reports" | "datasets" | "records"
  limit: number
  onPrevClick?: () => void
  onNextClick?: () => void
  onLimitChange?: (limit: number | string) => void
  pageCursors: Record<string, any>
}

export type SelectDropdownOption = {
  id?: number | string
  label?: string
  value: string
}

export const ROWS_PER_PAGE_OPTIONS = [
  {
    value: "5"
  },
  {
    value: "10"
  },
  {
    value: "25"
  },
  {
    value: "50"
  },
  {
    value: "100"
  }
]

export const SelectDropdown = ({
  selected,
  options,
  onSelect
}: {
  selected: string | number
  onSelect?: (option: SelectDropdownOption) => void
  options: Array<SelectDropdownOption>
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [selectedItemValue, setSelectedItemValue] = useState(selected)

  useEffect(() => {
    setSelectedItemValue(selected)
  }, [selected])

  const {
    refs: { setReference, setFloating, domReference },
    floatingStyles,
    context
  } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement: "top",
    transform: false,
    middleware: [offset(-1), flip(), shift()]
  })

  const click = useClick(context)
  const dismiss = useDismiss(context)

  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    dismiss
  ])

  const selectedItem = options.find(
    (option) => option.value == selectedItemValue
  )

  const selectedItemLabel =
    selectedItem?.label ?? selectedItem?.value ?? "- Select -"

  return (
    <section className={"relative"}>
      <section
        ref={setReference}
        role={"button"}
        {...getReferenceProps({
          className:
            "flex items-center gap-2 py-0.5 px-2 border-[0.5px] border-[#4B5563] dark:bg-[#1A1D21]"
        })}
      >
        <span className={"inline-block pointer-events-none"}>
          {selectedItemLabel}
        </span>

        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M6 9L1.5 4.39474L2.13 3.75L6 7.71053L9.87 3.75L10.5 4.39474L6 9Z"
            fill="currentColor"
          />
        </svg>
      </section>

      {isOpen && (
        <FloatingPortal>
          <ul
            ref={setFloating}
            {...getFloatingProps({
              className:
                "border-[0.5px] border-[#4B5563] bg-[#1A1D21] z-50 py-2"
            })}
            style={{
              ...floatingStyles,
              // 0.5px, x2, border consideration
              width: (domReference?.current?.clientWidth ?? 0) + 2
            }}
          >
            {options.map((option) => (
              <li
                key={option.id || option.value}
                onClick={() => {
                  setSelectedItemValue(option.value)
                  setIsOpen(false)
                  onSelect?.(option)
                }}
                role={"button"}
                className={classNames(
                  "p-2 text-white cursor-pointer text-xs hover:bg-[#1F2937]",
                  {
                    "bg-[#1F2937]": option.value == selectedItemValue
                  }
                )}
              >
                {option.label || option.value}
              </li>
            ))}
          </ul>
        </FloatingPortal>
      )}
    </section>
  )
}

const CursorPagination = ({
  limit,
  onPrevClick,
  onNextClick,
  type = "records",
  pageCursors,
  onLimitChange
}: CursorPaginationProps) => {
  const commonButtonClassName =
    "h-full flex justify-center items-center gap-2 hover:bg-gray-100 dark:hover:bg-[#4B5563]"
  const disabledButtonClassName =
    "h-full flex justify-center items-center gap-2 cursor-not-allowed px-2.5 !text-gray-300 dark:!text-gray-400"

  const disableNextButton = !pageCursors.next
  const disablePrevButton = !pageCursors.previous

  const prevButtonContent = (
    <>
      <span>
        <ArrowLeft size={12} />
      </span>
      <span className={"hidden md:inline-block"}>Previous</span>
    </>
  )

  const nextButtonContent = (
    <>
      <span className={"hidden md:inline-block"}>Next</span>
      <span className={"rotate-180"}>
        <ArrowLeft size={12} />
      </span>
    </>
  )

  const getRecordSummary = (limit: number, totalCount: number, type: string) =>
    `${totalCount < limit ? totalCount : limit} of ${totalCount} ${type}`

  const dataIsLoaded = pageCursors?.totalCount
  const skeletonColor = "#2D3036"
  const skeletonHighlighterColor = useMemo(
    () => tinycolor(skeletonColor).lighten(5).toString(),
    []
  )

  return (
    <section
      role={"navigation"}
      className={
        "py-2.5 px-5 flex justify-center items-center gap-4 font-jakarta text-[12px] leading-5 text-gray-500 dark:text-gray-400"
      }
    >
      {dataIsLoaded ? (
        <section className={"inline-flex items-center gap-3 mr-auto"}>
          <section className={"flex gap-2.5"}>
            <span>Rows per page</span>

            <SelectDropdown
              options={ROWS_PER_PAGE_OPTIONS}
              selected={limit}
              onSelect={({ value }) => onLimitChange?.(value)}
            ></SelectDropdown>
          </section>
          <span className={"hidden md:inline-block grow mr-auto"}>
            {getRecordSummary(limit, pageCursors?.totalCount ?? 0, type)}
          </span>
        </section>
      ) : (
        <Skeleton
          containerClassName={"inline-block grow mr-auto"}
          borderRadius={0}
          width={80}
          baseColor={skeletonColor}
          highlightColor={skeletonHighlighterColor}
        />
      )}

      <section>
        {dataIsLoaded ? (
          <ul
            className={
              "flex items-center divide-x-[0.5px] dark:divide-[#4B5563] border-[0.5px] dark:border-[#4B5563]"
            }
          >
            <li className={"h-6"}>
              <button
                className={classNames(
                  `text-gray-500 dark:text-white px-2.5`,
                  disablePrevButton
                    ? disabledButtonClassName
                    : commonButtonClassName
                )}
                disabled={disablePrevButton}
                onClick={onPrevClick}
              >
                {prevButtonContent}
              </button>
            </li>

            <li className={"h-6"}>
              <button
                className={classNames(
                  `text-gray-500 dark:text-white px-2.5`,
                  disableNextButton
                    ? disabledButtonClassName
                    : commonButtonClassName
                )}
                disabled={disableNextButton}
                onClick={onNextClick}
              >
                {nextButtonContent}
              </button>
            </li>
          </ul>
        ) : (
          <Skeleton
            containerClassName={"inline-block"}
            borderRadius={0}
            width={160}
            baseColor={skeletonColor}
            highlightColor={skeletonHighlighterColor}
          />
        )}
      </section>
    </section>
  )
}

export default CursorPagination
